import {
	CheckCircleFilled,
	CloseCircleFilled,
	SyncOutlined,
	WarningFilled,
} from '@ant-design/icons';
import { Checkbox } from 'antd';
import { FC } from 'react';
import {
	ValidationResult,
	ValidationStatus,
} from '../../../hooks/SearchTagsApi';
import { EditableRow } from '../types';

interface IProps {
	rows: EditableRow[];
	validations: ValidationResult[];
	confirmedOverwrites: Set<number>;
	onToggleOverwrite: (index: number, checked: boolean) => void;
}

const STATUS_ICON: Record<ValidationStatus, JSX.Element> = {
	ok: <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />,
	warning: <WarningFilled style={{ color: '#faad14', fontSize: 18 }} />,
	overwrite: <SyncOutlined style={{ color: '#fa8c16', fontSize: 18 }} spin={false} />,
	blocked: <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 18 }} />,
};

const STATUS_LABEL: Record<ValidationStatus, string> = {
	ok: 'OK',
	warning: 'Aviso',
	overwrite: 'Sobrescrever',
	blocked: 'Bloqueado',
};

export const ConfirmStep: FC<IProps> = ({
	rows,
	validations,
	confirmedOverwrites,
	onToggleOverwrite,
}) => {
	const counts = validations.reduce(
		(acc, v) => {
			acc[v.status] = (acc[v.status] || 0) + 1;
			return acc;
		},
		{ ok: 0, warning: 0, overwrite: 0, blocked: 0 } as Record<
			ValidationStatus,
			number
		>
	);

	const willImport =
		counts.ok +
		counts.warning +
		Array.from(confirmedOverwrites).length;

	return (
		<div>
			<div className='mb-3 text-sm'>
				<b>{willImport}</b> serão importados ·{' '}
				<b>{counts.overwrite}</b> sobrescrita(s){' '}
				{counts.overwrite > 0 && (
					<span className='text-gray-500'>
						(marque as que deseja confirmar)
					</span>
				)}{' '}
				· <b>{counts.warning}</b> com aviso ·{' '}
				<b>{counts.blocked}</b> bloqueada(s)
			</div>

			<div
				className='overflow-x-auto'
				style={{ maxHeight: 420, overflowY: 'auto' }}
			>
				<table className='w-full text-sm border-collapse'>
					<thead className='bg-gray-50 sticky top-0'>
						<tr>
							<th className='p-2 text-left w-10'>#</th>
							<th className='p-2 text-left w-10'></th>
							<th className='p-2 text-left w-28'>Status</th>
							<th className='p-2 text-left w-28'>Nacionalidade</th>
							<th className='p-2 text-left'>Documento</th>
							<th className='p-2 text-left'>Nome</th>
							<th className='p-2 text-left'>Email</th>
							<th className='p-2 text-left'>Detalhes</th>
						</tr>
					</thead>
					<tbody>
						{validations.map((v) => {
							const row = rows[v.index];
							if (!row) return null;
							return (
								<tr key={v.index} className='border-b align-top'>
									<td className='p-2'>{v.index + 1}</td>
									<td className='p-2'>{STATUS_ICON[v.status]}</td>
									<td className='p-2'>
										<div>{STATUS_LABEL[v.status]}</div>
										{v.status === 'overwrite' && (
											<Checkbox
												checked={confirmedOverwrites.has(v.index)}
												onChange={(e) =>
													onToggleOverwrite(
														v.index,
														e.target.checked
													)
												}
											>
												<span className='text-xs'>
													Confirmar
												</span>
											</Checkbox>
										)}
									</td>
									<td className='p-2'>{row.nationality || '—'}</td>
									<td className='p-2'>{row.document || '—'}</td>
									<td className='p-2'>{row.title || '—'}</td>
									<td className='p-2'>{row.email || '—'}</td>
									<td className='p-2'>
										{v.reasons.length === 0 ? (
											<span className='text-gray-400'>—</span>
										) : (
											<ul className='list-disc list-inside'>
												{v.reasons.map((r, i) => (
													<li
														key={i}
														className={
															v.status === 'blocked'
																? 'text-red-600'
																: 'text-yellow-700'
														}
													>
														{r.message}
													</li>
												))}
											</ul>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};
